import { PlusIcon } from "@phosphor-icons/react/dist/csr/Plus";
import { TrashIcon } from "@phosphor-icons/react/dist/csr/Trash";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { IBusiness } from "@/business/business";
import { GOOGLE_BUSINESS_CONNECTION_STATE } from "@/business/constants";
import { useGetGoogleBusinessAccountLocations } from "@/business/google-business-query";
import { useGetMyUser } from "@/user/query";
import { isAdminOrOwner } from "@/user/utils";
import { DeleteGoogleBusinessLocationModal } from "./delete-google-business-location-modal";
import { GoogleBusinessConnectionModal } from "./google-business-connection-modal";
import GoogleLogo from "@/assets/images/google-logo.svg";

interface GoogleBusinessConnectionProps {
  business: IBusiness;
}

const CONNECTION_STATE_TO_TEXT = {
  [GOOGLE_BUSINESS_CONNECTION_STATE.not_started]:
    "Connect your Google Business account so we can analyze your reviews and provide more insights.",
  [GOOGLE_BUSINESS_CONNECTION_STATE.connected]:
    "Your Google Business account is connected. Select the locations associated with this business to complete setup.",
};

export const GoogleBusinessConnection = ({
  business,
}: GoogleBusinessConnectionProps) => {
  const { data: user } = useGetMyUser();
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteLocationId, setDeleteLocationId] = useState<string | null>(null);

  const { data: accountLocations = [], isLoading: locationsLoading } =
    useGetGoogleBusinessAccountLocations(business.id);

  const connectionState = business.google_business_connection_state;
  const isConnected =
    connectionState === GOOGLE_BUSINESS_CONNECTION_STATE.connected;

  const hasLocations = !locationsLoading && accountLocations.length > 0;
  const canConnectAccount = isAdminOrOwner(user?.role);

  const connectionText =
    CONNECTION_STATE_TO_TEXT[
      connectionState as keyof typeof CONNECTION_STATE_TO_TEXT
    ] || CONNECTION_STATE_TO_TEXT[GOOGLE_BUSINESS_CONNECTION_STATE.not_started];

  const handleGoogleBusinessLink = async () => {
    if (!isConnected) {
      if (!business.id || !user?.email) {
        toast.error("Missing required information", {
          description: "Business ID and user email are required",
        });
        return;
      }

      setIsLoading(true);

      try {
        const apiBase = import.meta.env.VITE_API_URL;
        if (!apiBase) {
          toast.error("Couldn't connect to Google Business", {
            description: "Refresh the page and try again.",
          });
          setIsLoading(false);
          return;
        }
        const params = new URLSearchParams({
          state: JSON.stringify({
            business_id: business.id,
            user_email: user.email,
          }),
        });
        window.location.href = `${apiBase}/auth/google_oauth2?${params.toString()}`;
      } catch (error) {
        console.error("Google OAuth initiation error:", error);
        toast.error("Failed to initiate Google Business connection", {
          description:
            error instanceof Error ? error.message : "Please try again",
        });
        setIsLoading(false);
      }
    }
  };

  const handleAddLocation = () => {
    setIsModalOpen(true);
  };

  const handleDeleteLocation = (locationId: string) => {
    setDeleteLocationId(locationId);
  };

  const handleCloseDeleteModal = () => {
    setDeleteLocationId(null);
  };

  if (!user) {
    return null;
  }

  return (
    <Stack align="start" gap={6} className="pb-6">
      <Text variant="title">Google Business integration</Text>

      {!isConnected && <Text>{connectionText}</Text>}

      {isConnected && !hasLocations && <Text>{connectionText}</Text>}

      {canConnectAccount && !isConnected && (
        <Button
          onClick={handleGoogleBusinessLink}
          disabled={!business.id || !user?.email || isLoading}
          loading={isLoading}
          variant="outline"
          className="w-fit"
          type="button"
        >
          {!isLoading && (
            <img
              data-icon="inline-start"
              src={GoogleLogo}
              alt="Google"
              className="h-5 w-5"
            />
          )}
          Connect Google Business
        </Button>
      )}

      {isConnected && (
        <Stack align="start" gap={4}>
          {!locationsLoading &&
            accountLocations.map((location) => (
              <Stack
                align="start"
                gap={4}
                key={location.id}
                className="w-full rounded-xl bg-neutral px-4 py-3"
              >
                <Stack
                  className="w-full"
                  direction="row"
                  gap={4}
                  align="center"
                  justify="between"
                >
                  <Stack direction="row" gap={4} align="center">
                    <img src={GoogleLogo} alt="Google" className="h-5 w-5" />
                    <Stack align="start" gap={2}>
                      <Text variant="label">
                        {location.title || "Location"}
                      </Text>
                      <Text tone="muted">{location.location}</Text>
                      <Text tone="muted">Account: {location.account}</Text>
                    </Stack>
                  </Stack>
                  {canConnectAccount && (
                    <Button
                      variant="outline"
                      size="sm"
                      aria-label="Remove location"
                      onClick={() => handleDeleteLocation(location.id)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  )}
                </Stack>
              </Stack>
            ))}

          {canConnectAccount && (
            <Button
              onClick={handleAddLocation}
              variant="outline"
              className="w-fit"
              type="button"
            >
              <PlusIcon className="h-4 w-4" />
              {hasLocations ? "Add another location" : "Add location"}
            </Button>
          )}
        </Stack>
      )}

      {!canConnectAccount && (
        <Text tone="muted">
          Only managers and admins can manage Google Business connections.
        </Text>
      )}

      {isModalOpen && (
        <GoogleBusinessConnectionModal
          business={business}
          onClose={() => setIsModalOpen(false)}
        />
      )}

      <DeleteGoogleBusinessLocationModal
        businessId={business.id}
        locationId={deleteLocationId}
        onClose={handleCloseDeleteModal}
      />
    </Stack>
  );
};
